import { Component, computed, inject, linkedSignal, resource, signal, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalDialogComponent } from 'src/app/controls/components/advanced/modal-dialog/modal-dialog.component';
import { ButtonComponent } from 'src/app/controls/components/basic/button/button.component';
import { ButtonPrimaryDirective } from 'src/app/controls/directives/button-primary.directive';
import { ButtonSecondaryDirective } from 'src/app/controls/directives/button-secondary.directive';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { FormsModule } from '@angular/forms';
import { DocumentConfig } from 'src/app/developer/models/document-config';
import { DeveloperService } from 'src/app/developer/services/developer.service';
import { DocumentTypeOutput, TransportType } from 'src/app/partnerportal/models/partner-onboarding';
import { SingleselectboxComponent } from 'src/app/controls/components/basic/singleselectbox/singleselectbox.component';
import { PartnerService } from '../../services/partner.service';
import { PartnerSelectorComponent } from '../../partner-selector.component';
import { Partner } from '../../models';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';

@Component({
  selector: 'app-partner-onboarding-accept-documenttype-dialog',
  standalone: true,
  imports: [ModalDialogComponent, FormsModule, ButtonComponent, ButtonPrimaryDirective, ButtonSecondaryDirective, ModalDialogComponent, LayoutColumnComponent, LayoutRowComponent, SingleselectboxComponent, TextboxComponent, PartnerSelectorComponent],
  templateUrl: './partner-onboarding-accept-documenttype-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './partner-onboarding-accept-documenttype-dialog.component.scss'
})
export class PartnerOnboardingAcceptDocumenttypeDialogComponent {
  private dialog = inject(NgbActiveModal);
  private developerService = inject(DeveloperService);
  private partnerService = inject(PartnerService);

  public documentType = signal<DocumentTypeOutput>(null);
  public direction = signal<string>(null);
  public onboardingPartnerId = signal<number>(null);
  public internalPartnerId = signal<number>(null);
  public dialigResult = signal<PartnerOnboardingAcceptDocumentTypeDialogResult>(null);
  public transportOptions = signal<Record<string, string>>({});

  documentConfigResource = resource({
    params: () => ({}),
    loader: () => this.developerService.getDocumentConfigs(),
  });

  formatResource = resource({
    params: () => ({}),
    loader: () => this.developerService.getFormats(),
  });

  variantResource = resource({
    params: () => ({}),
    loader: () => this.developerService.getVariants(),
  });

  versionResource = resource({
    params: () => ({}),
    loader: () => this.developerService.getVariantVersions(),
  });

  onboardingPartnerResource = resource({
    params: () => ({}),
    loader: () => this.partnerService.getPartner(this.onboardingPartnerId()),
  });

  internalPartnerResource = resource({
    params: () => ({}),
    loader: () => this.partnerService.getPartner(this.internalPartnerId()),
  });

  documentConfigs = computed(() => this.documentConfigResource.value() );
  formats = computed(() => this.formatResource.value() );
  variants = computed(() => this.variantResource.value() );
  versions = computed(() => this.versionResource.value() );
  onboardingPartner = computed(() => this.onboardingPartnerResource.value() );
  internalPartner = computed(() => this.internalPartnerResource.value() );
  transportType = computed(() => {
    switch (this.documentType()?.Transport?.Type) {
      case TransportType.AS2:
        return 'AS2';
      case TransportType.SFTPCustomerEndpoint:
      case TransportType.SFTPPartnerEndpoint:
        return 'SFTP';
      case TransportType.FTPCustomerEndpoint:
      case TransportType.FTPPartnerEndpoint:
        return 'FTP';
      case TransportType.IntegratedSFTP:
      case TransportType.IntegratedFTP:
        return 'Integrated';

      default:
        return this.documentType()?.Transport?.Type ?? '';
    }
  });

  //Ingoing document configs
  documentConfigsIn = computed(() => {
    let configs = this.documentConfigs()?.filter(config => config.Direction.toLocaleLowerCase() === 'in');
    switch (this.direction()) {
      case 'in': // received from partner
        configs = configs?.filter(config => config.DistributionObjectId === (this.documentType().Id ?? config.DistributionObjectId)  && config.FormatId === (this.documentType().Format.Id ?? config.FormatId));
        break;
      case 'out':
        configs = configs?.filter(config => config.DistributionObjectId === (this.documentType().Id ?? config.DistributionObjectId));
        break;
      default:
        configs = [];
        break;
    }
    return this.toDisplayConfigs(configs);
  });

  //Outgoing document configs
  documentConfigsOut = computed(() => {
    let configs = this.documentConfigs()?.filter(config => config.Direction.toLocaleLowerCase() === 'out');
    switch (this.direction()) {
      case 'in':
        configs = configs?.filter(config => config.DistributionObjectId === (this.documentType().Id ?? config.DistributionObjectId));
        break;
      case 'out': // sent to partner
        configs = configs?.filter(config => config.DistributionObjectId === (this.documentType().Id ?? config.DistributionObjectId) && config.FormatId === (this.documentType().Format.Id ?? config.FormatId));
        break;
      default:
        configs = [];
        break;
    }
    return this.toDisplayConfigs(configs);
  });

  //Selected ingoing document config
  selectedDocConfigIn = linkedSignal<DocumentConfigDisplay[], DocumentConfigDisplay>( {
    source: this.documentConfigsIn,
    computation: (newConfigs) => {
      //If a format is known try to select a matching config, otherwise the first config
      const formatId = this.documentType().Format?.Id;
        if (newConfigs?.some(c => c.FormatId === formatId)) {
          return newConfigs.filter(c => c.FormatId === formatId).sort((a, b) => a.DocumentConfigId - b.DocumentConfigId)[0]
        }
        else {
          if (this.direction() === 'out') { //Always select first on customer side of CDM
            return newConfigs?.sort((a, b) => a.DocumentConfigId - b.DocumentConfigId)[0];
          }
          else  {
            return null;
          }
        }
    }
  });

  //Selected outgoing document config
  selectedDocConfigOut = linkedSignal<DocumentConfigDisplay[], DocumentConfigDisplay>( {
    source: this.documentConfigsOut,
    computation: (newConfigs) => {
      //If a format is known try to select a matching config, otherwise the first config
      const formatId = this.documentType().Format?.Id;
        if (newConfigs?.some(c => c.FormatId === formatId)) {
          return newConfigs.filter(c => c.FormatId === formatId).sort((a, b) => a.DocumentConfigId - b.DocumentConfigId)[0];
        }
        else {
          if (this.direction() === 'in') { //Always select first on customer side of CDM
            return newConfigs?.sort((a, b) => a.DocumentConfigId - b.DocumentConfigId)[0];
          }
          else  {
            return null;
          }
        }
    }
  });

  //Selected sending partner
  selectedPartnerIn = linkedSignal<Partner, Partner>( {
    source: () => {
      if (this.direction() === 'in') {
        return this.onboardingPartner();
      }
      else {
        return this.internalPartner();
      }
    },
    computation: (partner) => partner
  });

  //Selected receiving partner
  selectedPartnerOut = linkedSignal<Partner, Partner>( {
    source: () => {
      if (this.direction() === 'in') {
        return this.internalPartner();
      }
      else {
        return this.onboardingPartner();
      }
    },
    computation: (partner) => partner
  });

  transportFields = computed(() => this.documentType()?.Transport?.TransportFields ?? []);

  filenameExample = computed(() => this.transportFields()?.find(f => f.Name === 'FileNameExample')?.Value ?? '');

  isValid = computed(() => 
    this.selectedPartnerIn()?.PartnersId > 0 && 
    this.selectedPartnerOut()?.PartnersId > 0 && 
    this.selectedDocConfigIn()?.DocumentConfigId > 0 && 
    this.selectedDocConfigOut()?.DocumentConfigId > 0
  );

  result = computed(() => {
    const result: PartnerOnboardingAcceptDocumentTypeResult = {
      partnerInId: this.selectedPartnerIn()?.PartnersId,
      partnerOutId: this.selectedPartnerOut()?.PartnersId,
      documentConfigInId: this.selectedDocConfigIn()?.DocumentConfigId,
      documentConfigOutId: this.selectedDocConfigOut()?.DocumentConfigId,
      dialogResult: this.dialigResult(),
      transportOptions: this.transportOptions()
    }
    return result;
  });

  updateTransportOptions(key: string, value: string) {
    const currentOptions = this.transportOptions() ?? {};
    const newOptions = {...currentOptions, [key]: value};
    this.transportOptions.set(newOptions);
  }

  updateSelectedDocumentConfigIn(config) {
    const newConfig = config as DocumentConfigDisplay;
    this.selectedDocConfigIn.set(newConfig);
  }

  updateSelectedDocumentConfigOut(config) {
    const newConfig = config as DocumentConfigDisplay;
    this.selectedDocConfigOut.set(newConfig);
  }

  accept() {
    this.dialigResult.set(PartnerOnboardingAcceptDocumentTypeDialogResult.Accept);
    this.dialog.close(this.result());
  }

  acceptAndCreate() {
    this.dialigResult.set(PartnerOnboardingAcceptDocumentTypeDialogResult.AcceptAndCreate);
    this.dialog.close(this.result());
  }

  cancel() {
    this.dialigResult.set(PartnerOnboardingAcceptDocumentTypeDialogResult.Cancel);
    this.dialog.dismiss(this.result());
  }

  private toDisplayConfigs(configs: DocumentConfig[]): DocumentConfigDisplay[] {
    return configs?.map(c => {
      const format = this.formats()?.find(f => f.FormatsId === c.FormatId);
      const variant = this.variants()?.find(v => v.VariantsId === c.VariantId);
      const version = this.versions()?.find(vv => vv.VariantVersionsId === c.VariantVersionsId);
      const displayName = [format?.FormatName, variant?.VariantName, version?.VariantVersionName].join(' / ');
      return ({...c, DisplayName: displayName}) 
    });
  }
}

export interface PartnerOnboardingAcceptDocumentTypeResult {
  partnerInId: number;
  partnerOutId: number;
  documentConfigInId: number;
  documentConfigOutId: number;
  dialogResult: PartnerOnboardingAcceptDocumentTypeDialogResult;
  transportOptions?: Record<string, string>;
}

export enum PartnerOnboardingAcceptDocumentTypeDialogResult {
  Accept,
  AcceptAndCreate,
  Cancel
}

export interface DocumentConfigDisplay extends DocumentConfig {
  DisplayName: string;
}
